import { useTranslation } from 'react-i18next'
import {
  Award, Sparkles, FileText, Clock, Users, User as UserIcon,
  RefreshCw, AlarmClock, Trophy, Hourglass, ArrowRight, Play,
} from 'lucide-react'
import { Chip, Button, GlassCard, SubjectGlyph } from '@/components/aurora'

function examStatus(exam) {
  const now = Date.now()
  if (exam.start_time && new Date(exam.start_time).getTime() > now) return 'locked'
  if (exam.end_time   && new Date(exam.end_time).getTime()   < now) return 'expired'
  return 'open'
}

function fmt(iso, locale) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(locale || undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function countdownTo(iso, locale, t) {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return ''
  const days  = Math.floor(ms / 86_400_000)
  const hours = Math.floor((ms % 86_400_000) / 3_600_000)
  if (days > 0) return `${days} ${t('time.daysShort', 'kun')} ${hours} ${t('time.hoursShort', 'soat')}`
  const mins = Math.floor((ms % 3_600_000) / 60_000)
  return `${hours} ${t('time.hoursShort', 'soat')} ${mins} ${t('time.minutesShort', 'daq')}`
}

export default function ExamCard({ exam, isNew = false, onStart, onResult, onLeaderboard }) {
  const { t, i18n } = useTranslation()
  const st       = examStatus(exam)
  const isOpen   = st === 'open'
  const isLocked = st === 'locked'
  const isExpired = st === 'expired'

  const r             = exam.user_result
  const hasResult     = !!r
  const attemptsLeft  = (exam.allowed_attempts ?? 1) - (hasResult ? 1 : 0)
  const canRetake     = isOpen && hasResult && attemptsLeft > 0

  let primaryBtn = null
  if (hasResult) {
    primaryBtn = (
      <Button variant="green" onClick={onResult}>
        {t('exams.viewResult', 'Natija')}
      </Button>
    )
  } else if (isOpen) {
    primaryBtn = (
      <Button onClick={onStart}>
        <Play size={14} strokeWidth={2} />
        {t('exams.start', 'Boshlash')}
        <ArrowRight size={14} strokeWidth={2} />
      </Button>
    )
  }

  return (
    <GlassCard hover dim={isLocked || (isExpired && !hasResult)} className="px-5 py-4">
      <div className="grid grid-cols-[auto_1fr_auto] gap-4 items-center max-md:grid-cols-[auto_1fr]">
        <SubjectGlyph subject={exam.primary_subject} examTitle={exam.title} size="md" />

        <div className="min-w-0">
          {/* Title + chips */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="m-0 text-base font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>
              {exam.title}
            </h3>
            {exam.is_national_cert && (
              <Chip variant="cert"><Award size={11} strokeWidth={2} /> {t('exams.nationalCert', 'Sertifikat')}</Chip>
            )}
            {isNew && (
              <Chip variant="new"><Sparkles size={11} strokeWidth={2} /> {t('exams.new', 'Yangi')}</Chip>
            )}
            {isExpired && (
              <Chip variant="dim">{t('exams.expiredLabel', 'Yakunlangan')}</Chip>
            )}
          </div>

          {/* Description */}
          {exam.description && (
            <p className="text-[13px] leading-relaxed mb-2 line-clamp-1" style={{ color: 'var(--ink-2)' }}>
              {exam.description}
            </p>
          )}

          {/* Meta row */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px]" style={{ color: 'var(--ink-3)' }}>
            {exam.question_count != null && (
              <span className="inline-flex items-center gap-1.5">
                <FileText size={11} strokeWidth={2} /> {exam.question_count} {t('exams.questionsShort', 'savol')}
              </span>
            )}
            {exam.time_limit_minutes && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={11} strokeWidth={2} /> {exam.time_limit_minutes} {t('exams.minutesShort', 'daq')}
              </span>
            )}
            {exam.participant_count != null && (
              <span className="inline-flex items-center gap-1.5">
                <Users size={11} strokeWidth={2} /> {exam.participant_count} {t('exams.participants', 'ishtirokchi')}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <UserIcon size={11} strokeWidth={2} /> {exam.teacher?.full_name || exam.teacher?.username}
            </span>
            {exam.allowed_attempts > 1 && (
              <span className="inline-flex items-center gap-1.5">
                <RefreshCw size={11} strokeWidth={2} /> {exam.allowed_attempts} {t('exams.attempts', 'urinish')}
              </span>
            )}
          </div>

          {/* State-specific time line */}
          {isOpen && exam.end_time && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[11px]" style={{ color: 'var(--ink-3)' }}>
              <AlarmClock size={11} strokeWidth={2} />
              {fmt(exam.end_time, i18n.language)} {t('exams.untilOpen', 'gacha ochiq')}
            </div>
          )}
          {isLocked && exam.start_time && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[12px]" style={{ color: 'var(--butter)' }}>
              <Hourglass size={12} strokeWidth={2} />
              {countdownTo(exam.start_time, i18n.language, t)} {t('exams.untilUnlock', "dan keyin ochiladi")} ({fmt(exam.start_time, i18n.language)})
            </div>
          )}
          {isExpired && exam.end_time && !hasResult && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[11px]" style={{ color: 'var(--ink-3)' }}>
              <Hourglass size={11} strokeWidth={2} />
              {fmt(exam.end_time, i18n.language)} {t('exams.endedAt', 'da yakunlangan')}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1.5 items-stretch max-md:col-start-2 max-md:flex-row max-md:flex-wrap">
          {primaryBtn}
          {canRetake && (
            <Button variant="ghost" onClick={onStart}>
              <RefreshCw size={14} strokeWidth={2} />
              {t('exams.retake', 'Qayta')} ({attemptsLeft})
            </Button>
          )}
          {exam.leaderboard_visible && (
            <button
              onClick={onLeaderboard}
              className="inline-flex items-center justify-center gap-1.5 px-2 py-1 text-[12.5px] transition-colors rounded-lg"
              style={{ color: 'var(--ink-3)', background: 'transparent', border: 0, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}
            >
              <Trophy size={14} strokeWidth={2} /> {t('exams.leaderboard', 'Reyting')}
            </button>
          )}
        </div>
      </div>
    </GlassCard>
  )
}
